import { useRef, useEffect } from 'react'
import { Chart, registerables } from 'chart.js'
import type { ReportLinkData } from '@/lib/api'

Chart.register(...registerables)

interface ReportChartProps {
  linkData: ReportLinkData
  reportType: string
}

export function ReportChart({ linkData, reportType }: ReportChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return
    if (chartRef.current) chartRef.current.destroy()

    const baselineData: number[] = []
    const actualData: number[] = []
    const labels: string[] = []

    const labelInterval = reportType === 'daily' ? 12 : 48

    linkData.data.forEach((item, index) => {
      baselineData.push(item.baseline || 0)
      actualData.push(item.actual || 0)
      if (index % labelInterval === 0) {
        labels.push(reportType === 'daily' ? (item.time || '') : (item.day || ''))
      } else {
        labels.push('')
      }
    })

    const ctx = canvasRef.current.getContext('2d')!
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Baseline',
            data: baselineData,
            borderColor: 'rgb(0, 0, 255)',
            backgroundColor: 'rgba(0, 0, 255, 0.1)',
            borderWidth: 1,
            pointRadius: 0,
            tension: 0,
            spanGaps: true,
          },
          {
            label: 'Actual',
            data: actualData,
            borderColor: 'rgb(255, 0, 0)',
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
            borderWidth: 1,
            pointRadius: 0,
            tension: 0,
            spanGaps: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'top' },
          tooltip: { mode: 'index', intersect: false },
        },
        scales: {
          x: {
            display: true,
            title: { display: true, text: reportType === 'daily' ? 'Time' : 'Day / Time' },
            ticks: { maxTicksLimit: 20 },
          },
          y: {
            display: true,
            title: { display: true, text: 'Travel Time (seconds)' },
          },
        },
      },
    })

    return () => {
      chartRef.current?.destroy()
    }
  }, [linkData, reportType])

  return <canvas ref={canvasRef} />
}
